import React, { useState } from "react";

import { ApolloCache } from "@apollo/client";
import classNames from "classnames";
import moment from "moment-timezone";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheckCircle,
  faExclamationTriangle,
} from "@fortawesome/pro-solid-svg-icons";
import {
  faEdit,
  faFlask,
  faPause,
  faPlay,
  faRedo,
  faTrashAlt,
} from "@fortawesome/pro-light-svg-icons";

import Button from "ui/Button";
import ModalContainer from "ui/ModalContainer";
import PanelItem from "ui/PanelItem";
import { DestinationLinks } from "features/Checks/AlertDestinationTag";

import { OrganizationIntegrationsQuery } from "../../gql/Query.generated";
import {
  useDeleteWebhookEndpointMutation,
  useTestWebhookDestinationMutation,
  useUpdateWebhookEndpointMutation,
} from "../gql/Mutation.generated";
import { EndpointHealthType, EndpointStatus, TimeAgo } from "../EndpointHealth";
import { WebhookSigningKeyType } from "../SigningKeys";
import WebhookForm, { TestOutcome, testFailureMessage } from "../WebhookForm";
import {
  DeliveryDetails,
  DeliveryLog,
  DeliveryType,
  ENDPOINT_NOUNS,
  EndpointKind,
  isRetryable,
} from "./Deliveries";
import {
  useRetryIncidentIoDeliveryMutation,
  useRetryWebhookDeliveryMutation,
} from "./Deliveries/gql/Mutation.generated";

type WebhookEndpointType =
  OrganizationIntegrationsQuery["getOrganizationDetails"]["webhookEndpoints"][number];

type View =
  | { kind: "overview" }
  | { kind: "edit" }
  | { kind: "delivery"; delivery: DeliveryType };

export type Notice = { kind: "success" | "error"; message: React.ReactNode };

const REFETCH_QUERIES = ["OrganizationIntegrations"];

function EndpointDetailModal({
  organizationSlug,
  endpoint,
  signingKeys,
  canEdit,
  onClose,
  onRemoved,
}: {
  organizationSlug: string;
  endpoint: WebhookEndpointType;
  signingKeys: WebhookSigningKeyType[];
  canEdit: boolean;
  onClose: () => void;
  onRemoved: () => void;
}) {
  const [view, setView] = useState<View>({ kind: "overview" });
  const [notice, setNotice] = useState<Notice | null>(null);
  const showOverview = () => setView({ kind: "overview" });

  return (
    <ModalContainer
      title={
        view.kind === "edit"
          ? `Edit Webhook: ${endpoint.name}`
          : `Webhook: ${endpoint.name}`
      }
      onClose={onClose}
    >
      {view.kind === "edit" ? (
        <div className="max-w-[640px] py-3">
          <WebhookForm
            organizationSlug={organizationSlug}
            endpoint={endpoint}
            signingKeys={signingKeys}
            signingKeyCreationError={null}
            onSaved={(message) => {
              setNotice({ kind: "success", message });
              showOverview();
            }}
            onCancel={showOverview}
          />
        </div>
      ) : view.kind === "delivery" ? (
        <div className="py-3">
          <Button bare variant="link" className="mb-3" onClick={showOverview}>
            &lsaquo; Back to all deliveries
          </Button>
          <DeliveryDetails
            kind="webhook"
            delivery={view.delivery}
            actions={
              canEdit && (
                <RetryAction
                  kind="webhook"
                  endpoint={endpoint}
                  delivery={view.delivery}
                  onRetried={(delivery) =>
                    setView({ kind: "delivery", delivery })
                  }
                />
              )
            }
          />
        </div>
      ) : (
        <div className="py-3">
          {notice && <NoticeMessage notice={notice} />}
          <EndpointOverview
            organizationSlug={organizationSlug}
            endpoint={endpoint}
            canEdit={canEdit}
            onEdit={() => {
              setNotice(null);
              setView({ kind: "edit" });
            }}
            onNotice={setNotice}
            onRemoved={onRemoved}
            onSelectDelivery={(delivery) =>
              setView({ kind: "delivery", delivery })
            }
          />
        </div>
      )}
    </ModalContainer>
  );
}

function EndpointOverview({
  organizationSlug,
  endpoint,
  canEdit,
  onEdit,
  onNotice,
  onRemoved,
  onSelectDelivery,
}: {
  organizationSlug: string;
  endpoint: WebhookEndpointType;
  canEdit: boolean;
  onEdit: () => void;
  onNotice: (notice: Notice | null) => void;
  onRemoved: () => void;
  onSelectDelivery: (delivery: DeliveryType) => void;
}) {
  const { sendTest, toggleEnabled, remove, busy } = useEndpointActions({
    organizationSlug,
    endpoint,
    onNotice,
    onRemoved,
  });

  return (
    <>
      <PanelItem label="URL">
        <span className="break-all font-query text-[13px]">{endpoint.url}</span>
      </PanelItem>
      {endpoint.headerNames.length > 0 && (
        <PanelItem label="Request headers">
          <span className="break-words">{endpoint.headerNames.join(", ")}</span>{" "}
          <span className="text-[#606771]">(values hidden)</span>
        </PanelItem>
      )}
      <EndpointHealthItems
        organizationSlug={organizationSlug}
        endpoint={endpoint}
      />
      {canEdit && (
        <div className="mb-4 flex flex-wrap items-center gap-x-6 gap-y-2">
          <Button bare variant="link" disabled={busy} onClick={sendTest}>
            <FontAwesomeIcon icon={faFlask} /> Send test
          </Button>
          <Button bare variant="link" disabled={busy} onClick={onEdit}>
            <FontAwesomeIcon icon={faEdit} /> Edit
          </Button>
          <Button bare variant="link" disabled={busy} onClick={toggleEnabled}>
            {endpoint.disabledAt ? (
              <>
                <FontAwesomeIcon icon={faPlay} /> Test and enable
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faPause} /> Disable
              </>
            )}
          </Button>
          <Button bare variant="danger" disabled={busy} onClick={remove}>
            <FontAwesomeIcon icon={faTrashAlt} /> Remove
          </Button>
        </div>
      )}
      <DeliveryLog
        organizationSlug={organizationSlug}
        kind="webhook"
        endpointId={endpoint.id}
        onSelect={onSelectDelivery}
      />
    </>
  );
}

// Status, use, and recent outcomes of an endpoint
export function EndpointHealthItems({
  organizationSlug,
  endpoint,
}: {
  organizationSlug: string;
  endpoint: EndpointHealthType & Pick<WebhookEndpointType, "destinations">;
}) {
  return (
    <div className="grid grid-cols-2 items-start gap-x-8 md:grid-cols-4">
      <PanelItem label="Status">
        <EndpointStatus endpoint={endpoint} fallback="Enabled" />
      </PanelItem>
      <PanelItem label="Used by">
        {endpoint.destinations.length > 0 ? (
          <DestinationLinks
            destinations={endpoint.destinations}
            organizationSlug={organizationSlug}
          />
        ) : (
          "No alert destinations"
        )}
      </PanelItem>
      <PanelItem label="Last delivered">
        {endpoint.lastSuccessAt != null ? (
          <TimeAgo ts={endpoint.lastSuccessAt} />
        ) : (
          "Never"
        )}
      </PanelItem>
      <PanelItem label="Last failed">
        <LastFailure endpoint={endpoint} />
      </PanelItem>
    </div>
  );
}

function LastFailure({ endpoint }: { endpoint: EndpointHealthType }) {
  if (endpoint.lastFailureAt == null) {
    return <>Never</>;
  }
  const recent = moment
    .unix(endpoint.lastFailureAt)
    .isAfter(moment().subtract(24, "hours"));
  return (
    <div className={classNames(recent && "text-[#c93a2a]")}>
      <TimeAgo ts={endpoint.lastFailureAt} />
      {endpoint.consecutiveFailures > 1 && (
        <div>{endpoint.consecutiveFailures} failures in a row</div>
      )}
    </div>
  );
}

function useEndpointActions({
  organizationSlug,
  endpoint,
  onNotice,
  onRemoved,
}: {
  organizationSlug: string;
  endpoint: WebhookEndpointType;
  onNotice: (notice: Notice | null) => void;
  onRemoved: () => void;
}) {
  const reportError = (message: string) => onNotice({ kind: "error", message });
  const [testEndpoint, testResult] = useTestWebhookDestinationMutation({
    variables: { organizationSlug, webhookEndpointId: endpoint.id },
    onError: (error) => reportError(`Test delivery failed: ${error.message}`),
  });
  // The result carries the changed fields, so the cached endpoint updates without
  // a refetch, which could fail after the update succeeded
  const [updateEndpoint, updateResult] = useUpdateWebhookEndpointMutation({
    onError: (error) => reportError(error.message),
  });
  const [deleteEndpoint, deleteResult] = useDeleteWebhookEndpointMutation({
    refetchQueries: REFETCH_QUERIES,
    onCompleted: onRemoved,
    onError: (error) => reportError(error.message),
  });

  // Reports a failed test, keeping the response body since it often says why the
  // receiver rejected the request. Returns the result only if the test succeeded.
  const runTest = async () => {
    const tested = await testEndpoint();
    const result = tested.data?.testWebhookDestination;
    if (result && result.status !== "succeeded") {
      onNotice({
        kind: "error",
        message: (
          <TestOutcome result={result}>
            Test delivery failed: {testFailureMessage(result)}
          </TestOutcome>
        ),
      });
      return null;
    }
    return result ?? null;
  };

  const sendTest = async () => {
    const result = await runTest();
    if (result) {
      onNotice({
        kind: "success",
        message: `Test delivered (HTTP ${result.responseStatus})`,
      });
    }
  };

  const enable = async () => {
    const result = await runTest();
    if (!result) {
      return;
    }
    const updated = await updateEndpoint({
      variables: { webhookEndpointId: endpoint.id, enabled: true },
      onError: (error) =>
        reportError(
          `Test delivered, but enabling the webhook failed: ${error.message}`,
        ),
    });
    if (updated.data) {
      onNotice({
        kind: "success",
        message: `Test delivered (HTTP ${result.responseStatus}), webhook enabled`,
      });
    }
  };

  const disable = async () => {
    const updated = await updateEndpoint({
      variables: { webhookEndpointId: endpoint.id, enabled: false },
    });
    if (updated.data) {
      onNotice({ kind: "success", message: "Webhook disabled" });
    }
  };

  return {
    sendTest: () => {
      onNotice(null);
      void sendTest();
    },
    toggleEnabled: () => {
      onNotice(null);
      void (endpoint.disabledAt ? enable() : disable());
    },
    remove: () => {
      if (window.confirm(`Remove the webhook "${endpoint.name}"?`)) {
        onNotice(null);
        void deleteEndpoint({ variables: { webhookEndpointId: endpoint.id } });
      }
    },
    busy: testResult.loading || updateResult.loading || deleteResult.loading,
  };
}

export function RetryAction({
  kind,
  endpoint,
  delivery,
  onRetried,
}: {
  kind: EndpointKind;
  endpoint: EndpointHealthType;
  delivery: DeliveryType;
  onRetried: (delivery: DeliveryType) => void;
}) {
  const [notice, setNotice] = useState<Notice | null>(null);
  const options = {
    // For the endpoint's last delivered and last failed times
    refetchQueries: REFETCH_QUERIES,
    onError: (error: Error) =>
      setNotice({ kind: "error", message: `Retry failed: ${error.message}` }),
  };
  const [retryWebhook, webhookRetry] = useRetryWebhookDeliveryMutation({
    ...options,
    update: (cache) => evictDeliveryLog(cache, "getWebhookDeliveries"),
  });
  const [retryIncidentIo, incidentIoRetry] = useRetryIncidentIoDeliveryMutation(
    {
      ...options,
      update: (cache) => evictDeliveryLog(cache, "getIncidentIoDeliveries"),
    },
  );
  const retry = async () => {
    const retried =
      kind === "webhook"
        ? (
            await retryWebhook({
              variables: { webhookDeliveryId: delivery.id },
            })
          ).data?.retryWebhookDelivery
        : (
            await retryIncidentIo({
              variables: { incidentIoDeliveryId: delivery.id },
            })
          ).data?.retryIncidentIoDelivery;
    if (retried) {
      setNotice(retryNotice(retried));
      onRetried(retried);
    }
  };
  const loading = webhookRetry.loading || incidentIoRetry.loading;
  const retryable = isRetryable(delivery);
  if (!notice && !retryable) {
    return null;
  }
  return (
    <div className="mt-4">
      {notice && <NoticeMessage notice={notice} />}
      {retryable && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <Button
            bare
            variant="link"
            disabled={loading || !!endpoint.disabledAt}
            onClick={() => {
              setNotice(null);
              void retry();
            }}
          >
            <FontAwesomeIcon icon={faRedo} /> Retry now
          </Button>
          {endpoint.disabledAt && (
            <span className="text-[#606771]">
              Enable the {ENDPOINT_NOUNS[kind]} to retry
            </span>
          )}
        </div>
      )}
    </div>
  );
}

function retryNotice(delivery: DeliveryType): Notice {
  return delivery.status === "succeeded"
    ? {
        kind: "success",
        message: `Retry delivered (HTTP ${delivery.responseStatus})`,
      }
    : {
        kind: "error",
        message: `Retry failed: ${delivery.errorMessage ?? delivery.status}`,
      };
}

// A retry can move the delivery between status filters, clear the cache
function evictDeliveryLog(cache: ApolloCache<object>, fieldName: string) {
  cache.evict({ id: "ROOT_QUERY", fieldName });
  cache.gc();
}

export function NoticeMessage({ notice }: { notice: Notice }) {
  const isError = notice.kind === "error";
  return (
    <div
      role={isError ? "alert" : "status"}
      className={classNames(
        "mb-4 break-words",
        isError ? "text-[#c93a2a]" : "text-[#398439]",
      )}
    >
      <FontAwesomeIcon
        className="mr-1"
        icon={isError ? faExclamationTriangle : faCheckCircle}
      />
      {notice.message}
    </div>
  );
}

export default EndpointDetailModal;
